import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  writeBatch,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { auth, db, storage } from './firebase';
import {
  requestFeedbackAuthority,
  submitFeedbackWithRecovery,
  type FeedbackAuthorityResponse,
} from './feedbackFunctionClient';
import {
  recoverFeedbackScreenshotFinalize,
  removeFeedbackScreenshotSet,
} from './feedbackScreenshotLifecycle';
import {
  uploadFeedbackScreenshotSet,
  uploadFeedbackScreenshotWithTimeout,
} from './feedbackUploadLifecycle';
import {
  drainFeedbackRetries,
  FEEDBACK_RETRY_READY_EVENT,
  feedbackRetryScreenshotPaths,
  queueFeedbackRetry,
  releaseFeedbackRetry,
  resolveFeedbackRetry,
  type FeedbackRetryItem,
} from './feedbackRetryQueue';
import {
  FEEDBACK_SCREENSHOT_LIMIT,
  feedbackScreenshotPaths,
  isFeedbackScreenshotFile,
} from './feedbackScreenshots.mjs';
import { functionHeaders } from './functionSecurity';
import { PHOTO_UPLOAD_TIMEOUT_MS, uploadBytesResumable } from './storageUpload';
import type { UserStatus } from './users';

export type AppFeedbackKind = 'issue' | 'suggestion';
export type AppFeedbackStatus = 'submitted' | 'in-progress' | 'complete' | 'no-go';
export type FeedbackEmailStatus = 'sent' | 'not-configured' | 'failed' | 'unavailable';
export type FeedbackScreenshotStatus = 'none' | 'pending' | 'ready' | 'failed';

const FEEDBACK_SUBMISSION_LEASE_MS = 30 * 60 * 1000;
const FEEDBACK_FIRST_RETRY_DELAY_MS = 15 * 60 * 1000;

export interface AppFeedbackDoc {
  id: string;
  kind: AppFeedbackKind;
  title: string;
  description: string;
  path: string;
  status: AppFeedbackStatus;
  publicResponse: string;
  expectedDate: string;
  screenshotAttached: boolean;
  /**
   * How many screenshots the report was sent with (up to 5), written by
   * feedbackNotify.mjs. Absent on every report from before multiple
   * screenshots. Read it only through attachedFeedbackScreenshotCount,
   * which trusts it once screenshotAttached is true.
   */
  screenshotCount?: number;
  createdAt: Timestamp | FieldValue;
  statusRevision: number;
  /**
   * Stamped server-side (feedbackNotify.mjs) on the transition INTO
   * complete, and re-stamped if a report is un-completed and completed
   * again. Absent on reports completed before this shipped and on anything
   * not complete. Drives the 7-day fresh-vs-older split of the board's Past
   * section; read it only through guarded helpers (feedbackCompletedAtMs).
   */
  completedAt?: Timestamp | FieldValue;
  /**
   * The feedback loop's approved reply (DECISIONS.md, "The feedback loop",
   * Bobbie 2026-08-15), written server-side by feedbackReply.mjs. Absent
   * until a reply lands; repliedBy is always the honest studio attribution
   * the server stamped, never anything the board renders on its own.
   */
  repliedAt?: Timestamp | FieldValue;
  replyText?: string;
  repliedBy?: string;
  /**
   * Every studio reply AFTER the first (Bobbie, 2026-10-19: "each reply shows
   * on their report"), oldest first, at most 10, written server-side by
   * feedbackReply.mjs. Absent until the studio sends one. Read it only
   * through readFeedbackFollowUps (lib/feedbackFollowUps.mjs), which guards
   * the shape and the timestamps.
   */
  followUps?: { at: Timestamp | FieldValue; text: string; by: string }[];
}

export interface AppFeedbackPrivateDoc {
  id: string;
  createdBy: string;
  /** The FIRST screenshot, the one field every older reader knows. */
  screenshotPath: string | null;
  /**
   * Every screenshot path, the first included (up to 5). Absent on a report
   * from before multiple screenshots; read through
   * storedFeedbackScreenshotPaths, which falls back to screenshotPath.
   */
  screenshotPaths?: string[];
  screenshotStatus: FeedbackScreenshotStatus;
  createdAt: Timestamp | FieldValue;
  /**
   * Snapshotted by the email claim (feedbackNotify.mjs) so Bobbie knows who
   * to reply to. Director-only surface: this collection is Director-read in
   * the rules, so a member board can never see it.
   */
  reporterName?: string;
}

export interface NewAppFeedbackInput {
  kind: AppFeedbackKind;
  title: string;
  description: string;
  path: string;
  userStatus: UserStatus;
  /** Up to 5 screenshots, sent in this order. */
  screenshots?: readonly File[];
  /** Told which screenshot is sending, so the form can say how far along it is. */
  onSendingScreenshot?: (number: number, total: number) => void;
}

export interface AppFeedbackSubmissionResult {
  id: string;
  emailStatus: FeedbackEmailStatus;
  screenshotStatus: FeedbackScreenshotStatus;
  retryQueued: boolean;
}

function clean(value: string, max: number): string {
  return value.trim().slice(0, max);
}

async function callFeedbackFunction(
  body: Record<string, unknown>,
): Promise<FeedbackAuthorityResponse> {
  const user = auth?.currentUser;
  if (!user) throw new Error('Sign in before updating app feedback.');
  return requestFeedbackAuthority({
    fetchImpl: fetch,
    getHeaders: () => functionHeaders(user),
    body,
  });
}

function submissionError(code: string | undefined, status: number): Error {
  if (code === 'active_account_required') return new Error('This account is not active yet.');
  if (code === 'report_conflict') return new Error('This feedback could not be safely retried. Refresh and try again.');
  if (code === 'rate_limited' || status === 429) return new Error('Feedback is busy right now. Wait a few minutes and try again.');
  return new Error('We could not save this feedback right now. Try again.');
}

async function submitFeedbackRecord(
  reportId: string,
  input: Pick<NewAppFeedbackInput, 'kind' | 'title' | 'description' | 'path'>,
  screenshotCount: number,
): Promise<FeedbackEmailStatus> {
  const outcome = await submitFeedbackWithRecovery(reportId, {
    submit: (id) => callFeedbackFunction({
      action: 'submit',
      reportId: id,
      kind: input.kind,
      title: input.title,
      description: input.description,
      path: input.path,
      // hasScreenshot keeps its old meaning for the server, and alone it
      // means one. The count goes only when there are more than one, so a
      // report with none or one sends exactly the body the function took
      // before multiple screenshots: a functions rollback leaves this tab
      // able to send everything but a multi-screenshot report.
      hasScreenshot: screenshotCount > 0,
      ...(screenshotCount > 1 ? { screenshotCount } : {}),
    }),
    reportExists: async (id) => {
      if (!db) return false;
      const snapshot = await getDoc(doc(db, 'appFeedback', id));
      return snapshot.exists();
    },
    notify: (id) => notifyBobbie(id),
  });
  if (outcome.ok) return outcome.email;
  throw submissionError(outcome.error, outcome.status);
}

async function notifyBobbie(reportId: string): Promise<FeedbackEmailStatus> {
  const user = auth?.currentUser;
  if (!user) return 'unavailable';
  let lastStatus: FeedbackEmailStatus = 'unavailable';
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const result = await callFeedbackFunction({ reportId });
      if (!result.ok) {
        lastStatus = 'failed';
        continue;
      }
      const email = result.body.email ?? 'failed';
      if (email === 'sent' || email === 'not-configured') return email;
      lastStatus = email;
    } catch {
      lastStatus = 'unavailable';
    }
  }
  return lastStatus;
}

function feedbackRefs(reportId: string) {
  if (!db) return null;
  return {
    publicRef: doc(db, 'appFeedback', reportId),
    privateRef: doc(db, 'appFeedbackPrivate', reportId),
  };
}

async function removeFeedbackUpload(path: string): Promise<void> {
  if (!storage) throw new Error('Storage not initialized.');
  try {
    await deleteObject(ref(storage, path));
  } catch (error) {
    const code = (error as { code?: unknown })?.code;
    if (code !== 'storage/object-not-found') throw error;
  }
}

/**
 * Settle a report's screenshots as one set: either the paired finalize write
 * landed and every one of them is attached, or the report is marked failed
 * and every file it may hold is removed. A file that never uploaded is simply
 * not found, which is fine.
 */
async function reconcileFeedbackScreenshot(
  reportId: string,
  screenshotPaths: readonly string[],
): Promise<'ready' | 'failed' | 'pending'> {
  if (!db || !storage) return 'pending';
  if (screenshotPaths.length === 0) return 'failed';
  const refs = feedbackRefs(reportId);
  if (!refs) return 'pending';
  const firestore = db;
  const screenshotWasFinalized = async () => {
    try {
      const snapshot = await getDoc(refs.publicRef);
      return snapshot.exists() && snapshot.data().screenshotAttached === true;
    } catch {
      return false;
    }
  };
  return recoverFeedbackScreenshotFinalize({
    isFinalized: screenshotWasFinalized,
    markFailed: () => runTransaction(firestore, async (transaction) => {
      const snapshot = await transaction.get(refs.publicRef);
      if (snapshot.exists() && snapshot.data().screenshotAttached === true) return 'ready' as const;
      transaction.update(refs.privateRef, {
        screenshotStatus: 'failed',
      });
      return 'failed' as const;
    }),
    removeUpload: () => removeFeedbackScreenshotSet(screenshotPaths, removeFeedbackUpload),
  });
}

let feedbackOutboxDrain: Promise<void> | null = null;

export function retryFeedbackOutbox(uid: string): Promise<void> {
  if (feedbackOutboxDrain) return feedbackOutboxDrain;
  if (!auth?.currentUser || auth.currentUser.uid !== uid) return Promise.resolve();
  feedbackOutboxDrain = drainFeedbackRetries(uid, {
    retryEmail: (item) => notifyBobbie(item.reportId),
    retryScreenshot: (item) => {
      const paths = feedbackRetryScreenshotPaths(item);
      return paths.length > 0
        ? reconcileFeedbackScreenshot(item.reportId, paths)
        : Promise.resolve('failed');
    },
  }).finally(() => { feedbackOutboxDrain = null; });
  return feedbackOutboxDrain;
}

export async function submitAppFeedback(input: NewAppFeedbackInput): Promise<AppFeedbackSubmissionResult> {
  if (!db || !storage || !auth?.currentUser) throw new Error('Sign in before sending feedback.');
  if (input.userStatus !== 'active') throw new Error('This account is not active yet.');

  const title = clean(input.title, 120);
  const description = clean(input.description, 1600);
  if (!title) throw new Error('Add a short title first.');
  if (!description) throw new Error('Tell us what happened or what you would like to see.');

  const screenshots = [...(input.screenshots ?? [])];
  if (screenshots.length > FEEDBACK_SCREENSHOT_LIMIT) {
    throw new Error(`You can add up to ${FEEDBACK_SCREENSHOT_LIMIT} screenshots. Remove one and try again.`);
  }
  if (screenshots.some((screenshot) => !isFeedbackScreenshotFile(screenshot))) {
    throw new Error('Screenshots must be images smaller than 8 MB.');
  }

  const uid = auth.currentUser.uid;
  const bucket = storage;
  const publicRef = doc(collection(db, 'appFeedback'));
  const privateRef = doc(db, 'appFeedbackPrivate', publicRef.id);
  const screenshotPaths = feedbackScreenshotPaths(uid, publicRef.id, screenshots.length);
  const path = clean(input.path || '/', 220) || '/';
  const emailStatus = await submitFeedbackRecord(publicRef.id, {
    kind: input.kind,
    title,
    description,
    path,
  }, screenshots.length);

  const retryItem: FeedbackRetryItem = {
    uid,
    reportId: publicRef.id,
    screenshotPath: screenshotPaths[0] ?? null,
    screenshotPaths,
    needsEmail: emailStatus !== 'sent',
    needsScreenshot: screenshotPaths.length > 0,
    // Queue before upload so a closed tab cannot lose recovery work, but lease
    // it while this live submit owns the upload/finalize sequence.
    retryAfter: Date.now() + FEEDBACK_SUBMISSION_LEASE_MS,
  };
  const retryQueued = (retryItem.needsEmail || retryItem.needsScreenshot)
    ? queueFeedbackRetry(retryItem)
    : false;

  let screenshotStatus: FeedbackScreenshotStatus = 'none';
  if (screenshots.length > 0) {
    try {
      // Every file to its own path, one at a time, and the first failure
      // stops the rest: the set attaches together or not at all (the
      // finalize below flips them all at once).
      await uploadFeedbackScreenshotSet(
        screenshots,
        screenshotPaths,
        (screenshot, screenshotPath) => uploadFeedbackScreenshotWithTimeout(
          (signal) => uploadBytesResumable(
            ref(bucket, screenshotPath),
            screenshot,
            {
              contentType: screenshot.type,
              cacheControl: 'private,max-age=0,no-store',
            },
            undefined,
            signal,
          ),
          PHOTO_UPLOAD_TIMEOUT_MS,
        ),
        input.onSendingScreenshot,
      );
      const finalize = writeBatch(db);
      finalize.update(publicRef, {
        screenshotAttached: true,
      });
      finalize.update(privateRef, {
        screenshotStatus: 'ready',
      });
      await finalize.commit();
      screenshotStatus = 'ready';
    } catch {
      // A lost Firestore acknowledgement can reject after the paired
      // pending-to-ready write already committed. Confirm public state before
      // cleanup so a valid private screenshot is never deleted or reported
      // as failed just because the response was lost.
      screenshotStatus = await reconcileFeedbackScreenshot(publicRef.id, screenshotPaths);
    }
    if (screenshotStatus !== 'pending') {
      resolveFeedbackRetry(uid, publicRef.id, { screenshot: true });
    }
  }

  if (emailStatus === 'sent') resolveFeedbackRetry(uid, publicRef.id, { email: true });
  if (retryQueued) {
    releaseFeedbackRetry(uid, publicRef.id, Date.now() + FEEDBACK_FIRST_RETRY_DELAY_MS);
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(FEEDBACK_RETRY_READY_EVENT));
  }
  return { id: publicRef.id, emailStatus, screenshotStatus, retryQueued };
}

export async function retryAppFeedbackDelivery(
  reportId: string,
  screenshotCount: number,
): Promise<AppFeedbackSubmissionResult> {
  if (!auth?.currentUser) throw new Error('Sign in before retrying feedback delivery.');
  const uid = auth.currentUser.uid;
  const screenshotPaths = feedbackScreenshotPaths(uid, reportId, screenshotCount);
  const retryQueued = queueFeedbackRetry({
    uid,
    reportId,
    screenshotPath: screenshotPaths[0] ?? null,
    screenshotPaths,
    needsEmail: true,
    needsScreenshot: screenshotPaths.length > 0,
    retryAfter: Date.now() + FEEDBACK_SUBMISSION_LEASE_MS,
  });

  const screenshotStatus = screenshotPaths.length > 0
    ? await reconcileFeedbackScreenshot(reportId, screenshotPaths)
    : 'none';
  if (screenshotStatus !== 'pending') {
    resolveFeedbackRetry(uid, reportId, { screenshot: true });
  }
  const emailStatus = await notifyBobbie(reportId);
  if (emailStatus === 'sent') resolveFeedbackRetry(uid, reportId, { email: true });
  if (retryQueued) {
    releaseFeedbackRetry(uid, reportId, Date.now() + FEEDBACK_FIRST_RETRY_DELAY_MS);
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(FEEDBACK_RETRY_READY_EVENT));
  }
  return { id: reportId, emailStatus, screenshotStatus, retryQueued };
}

export function useAppFeedback(enabled = true): {
  reports: AppFeedbackDoc[];
  loading: boolean;
  error: string | null;
} {
  const [reports, setReports] = useState<AppFeedbackDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !db) {
      setReports([]);
      setLoading(false);
      setError(enabled ? 'Firestore not initialized.' : null);
      return;
    }
    setLoading(true);
    const q = query(collection(db, 'appFeedback'), orderBy('createdAt', 'desc'), limit(250));
    return onSnapshot(
      q,
      (snapshot) => {
        setReports(snapshot.docs.map((item) => ({
          id: item.id,
          ...(item.data() as Omit<AppFeedbackDoc, 'id'>),
        })));
        setLoading(false);
        setError(null);
      },
      (snapshotError) => {
        setLoading(false);
        setError(snapshotError.message);
      }
    );
  }, [enabled]);

  return { reports, loading, error };
}

export function useAppFeedbackPrivate(enabled = false): {
  details: Record<string, AppFeedbackPrivateDoc>;
  error: string | null;
} {
  const [details, setDetails] = useState<Record<string, AppFeedbackPrivateDoc>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !db) {
      setDetails({});
      setError(null);
      return;
    }
    const q = query(collection(db, 'appFeedbackPrivate'), orderBy('createdAt', 'desc'), limit(250));
    return onSnapshot(
      q,
      (snapshot) => {
        setDetails(Object.fromEntries(snapshot.docs.map((item) => [
          item.id,
          { id: item.id, ...(item.data() as Omit<AppFeedbackPrivateDoc, 'id'>) },
        ])));
        setError(null);
      },
      (snapshotError) => setError(snapshotError.message)
    );
  }, [enabled]);

  return { details, error };
}

export async function updateAppFeedbackStatus(
  id: string,
  status: AppFeedbackStatus,
  publicResponse: string,
  expectedDate: string,
  expectedRevision: number,
): Promise<void> {
  if (!auth?.currentUser) throw new Error('Sign in before updating app feedback.');
  const response = clean(publicResponse, 1200);
  const date = status === 'in-progress' ? expectedDate : '';
  if (status === 'in-progress' && (!response || !/^\d{4}-\d{2}-\d{2}$/.test(date))) {
    throw new Error('In progress needs a public update and expected date.');
  }
  if (status === 'no-go' && !response) {
    throw new Error('No go needs a public reason.');
  }
  let result: FeedbackAuthorityResponse;
  try {
    result = await callFeedbackFunction({
      action: 'update-status',
      reportId: id,
      status,
      publicResponse: response,
      expectedDate: date,
      expectedRevision,
    });
  } catch {
    throw new Error('We could not save this feedback update right now. Try again.');
  }
  if (result.ok) return;
  if (result.body.error === 'stale_revision') {
    throw new Error('This feedback changed in another tab. Review the latest update and try again.');
  }
  if (result.body.error === 'report_not_found') {
    throw new Error('This feedback report no longer exists.');
  }
  if (result.body.error === 'director_required') {
    throw new Error('Only the Director can update app feedback.');
  }
  throw new Error('We could not save this feedback update right now. Try again.');
}
